import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { 
  X, 
  Calculator, 
  Sparkles, 
  CheckCircle2, 
  TrendingDown, 
  ArrowRight, 
  ShieldCheck, 
  Scale, 
  MessageCircle,
  Award
} from 'lucide-react';
import { formatINR } from '../data/products';

export const JEWELLERY_TIERS = [
  { id: 'plain', name: 'Plain Gold Chains & Bangles', ourWastage: 5.99, marketWastage: 14.0 },
  { id: 'casting', name: 'Modern Casting & CZ Jewellery', ourWastage: 7.99, marketWastage: 16.5 },
  { id: 'antique', name: 'Antique, Kundan & Bridal Sets', ourWastage: 8.99, marketWastage: 20.0 },
  { id: 'daily', name: 'Lightweight & Daily Wear Pieces', ourWastage: 6.49, marketWastage: 15.0 },
  { id: 'coins', name: '24KT Pure Bullion Coins & Bars', ourWastage: 1.49, marketWastage: 4.5 }
];

export default function WastageCalculatorModal() {
  const { 
    isWastageModalOpen, 
    setIsWastageModalOpen, 
    masterMetalRates, 
    liveGoldRates,
    storeSettings,
    addToast
  } = useShop();

  const [purity, setPurity] = useState('22KT');
  const [weight, setWeight] = useState(25);
  const [selectedTierId, setSelectedTierId] = useState('plain');

  if (!isWastageModalOpen) return null;

  // Resolve rates dynamically
  const rate24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || 7380;
  const rate22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || 6765;
  const rate18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || 5535;

  let currentRate = rate22K;
  if (purity === '24KT') currentRate = rate24K;
  else if (purity === '18KT') currentRate = rate18K;

  const currentTier = JEWELLERY_TIERS.find((t) => t.id === selectedTierId) || JEWELLERY_TIERS[0];

  const parsedWeight = Math.max(0.5, parseFloat(weight) || 1);
  const rawMetalValue = Math.round(parsedWeight * currentRate);

  // Traditional Jeweller calculation
  const marketWastageAmt = Math.round(rawMetalValue * (currentTier.marketWastage / 100));
  const marketTaxable = rawMetalValue + marketWastageAmt;
  const marketGst = Math.round(marketTaxable * 0.03);
  const marketTotal = marketTaxable + marketGst;

  // ML Jewellers / JewelCasa 24KT Club calculation
  const ourWastageAmt = Math.round(rawMetalValue * (currentTier.ourWastage / 100));
  const ourTaxable = rawMetalValue + ourWastageAmt;
  const ourGst = Math.round(ourTaxable * 0.03);
  const ourTotal = ourTaxable + ourGst;

  const totalSavings = Math.max(0, marketTotal - ourTotal);

  const handleWhatsAppOrder = () => {
    const phone = (storeSettings?.phone || storeSettings?.whatsapp || '919876543210').replace(/[^0-9]/g, '');
    const msg = `*💎 24KT CLUB WASTAGE INQUIRY - ML JEWELLERS*\n\n` +
      `• Purity: ${purity} (BIS Hallmarked)\n` +
      `• Category: ${currentTier.name}\n` +
      `• Net Weight: ${parsedWeight} Grams\n` +
      `• Live Rate: ₹${currentRate.toLocaleString('en-IN')}/g\n` +
      `• Digital Jeweller Wastage: ${currentTier.ourWastage}%\n` +
      `• Market Price: ${formatINR(marketTotal)}\n` +
      `• *Our Transparent Price:* ${formatINR(ourTotal)}\n` +
      `• *Savings:* ${formatINR(totalSavings)}\n\n` +
      `I want to lock this rate and place an order. Please confirm availability.`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
    addToast('Opening WhatsApp with your locked wastage estimate!', 'success');
  };

  return (
    <div 
      className="modal-backdrop"
      onClick={() => setIsWastageModalOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        overflowY: 'auto'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(145deg, #14131f 0%, #0c0b12 100%)',
          border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 35px rgba(212,175,55,0.15)',
          color: '#fff',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(212, 175, 55, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'var(--gold-gradient)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0a0a0f'
            }}>
              <Calculator size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <h3 style={{ fontSize: '1.15rem', margin: 0, fontWeight: 700, color: '#fff' }}>
                  24KT Club Wastage & Savings Calculator
                </h3>
                <span style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid #10b981',
                  color: '#34d399',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  padding: '0.1rem 0.45rem',
                  borderRadius: '999px',
                  textTransform: 'uppercase'
                }}>
                  SAVE ₹20L+
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Bharat Ka Pehla Digital Jeweller • Zero Hidden Making Charges
              </p>
            </div>
          </div>

          <button 
            onClick={() => setIsWastageModalOpen(false)}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.5rem' }}>
          
          {/* 1. Select Gold Purity */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>
              1. Select Gold Purity (Hallmarked)
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
              {[
                { label: '22KT (916 BIS)', id: '22KT', rate: rate22K, tag: 'Most Popular' },
                { label: '18KT (750 BIS)', id: '18KT', rate: rate18K, tag: 'Diamond Base' },
                { label: '24KT (999 Pure)', id: '24KT', rate: rate24K, tag: 'Bullion Grade' }
              ].map((p) => {
                const isSelected = purity === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPurity(p.id)}
                    type="button"
                    style={{
                      padding: '0.6rem 0.5rem',
                      borderRadius: 'var(--radius-sm)',
                      background: isSelected ? 'rgba(212, 175, 55, 0.18)' : 'rgba(255,255,255,0.04)',
                      border: isSelected ? '1.5px solid var(--gold-400)' : '1px solid rgba(255,255,255,0.08)',
                      color: isSelected ? 'var(--gold-300)' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{p.label}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--gold-400)', marginTop: '2px' }}>
                      {formatINR(p.rate)}/g
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Select Jewellery Category */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>
              2. Select Jewellery Design Type
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              {JEWELLERY_TIERS.map((tier) => {
                const isSelected = selectedTierId === tier.id;
                return (
                  <div
                    key={tier.id}
                    onClick={() => setSelectedTierId(tier.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.65rem 0.85rem',
                      borderRadius: 'var(--radius-sm)',
                      background: isSelected ? 'rgba(212, 175, 55, 0.12)' : 'rgba(255,255,255,0.03)',
                      border: isSelected ? '1px solid var(--gold-400)' : '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: isSelected ? '5px solid var(--gold-400)' : '2px solid rgba(255,255,255,0.3)',
                        background: '#0a0a0f'
                      }} />
                      <span style={{ fontSize: '0.84rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? '#fff' : 'var(--text-secondary)' }}>
                        {tier.name}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ fontSize: '0.72rem', color: '#a1a1aa', textDecoration: 'line-through' }}>
                        Market: {tier.marketWastage}%
                      </span>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        color: '#34d399',
                        background: 'rgba(16, 185, 129, 0.15)',
                        padding: '0.15rem 0.45rem',
                        borderRadius: '4px'
                      }}>
                        Our Wastage: {tier.ourWastage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. Weight Slider & Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                3. Enter Net Weight (Grams)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <input
                  type="number"
                  min="0.5"
                  step="0.1"
                  max="1000"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  style={{
                    width: '90px',
                    height: '34px',
                    lineHeight: '34px',
                    background: '#1a1926',
                    border: '1px solid var(--border-gold)',
                    borderRadius: '6px',
                    color: '#fff',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    padding: '0 6px'
                  }}
                />
                <span style={{ color: 'var(--gold-400)', fontWeight: 700, fontSize: '0.85rem' }}>g</span>
              </div>
            </div>

            {/* Slider */}
            <input
              type="range"
              min="1"
              max="200"
              step="1"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              style={{
                width: '100%',
                accentColor: 'var(--gold-400)',
                cursor: 'pointer'
              }}
            />

            {/* Quick Presets */}
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {[8, 15, 25, 50, 100].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setWeight(preset)}
                  style={{
                    padding: '0.2rem 0.6rem',
                    borderRadius: '4px',
                    background: weight === preset ? 'var(--gold-gradient)' : 'rgba(255,255,255,0.06)',
                    color: weight === preset ? '#000' : 'var(--text-secondary)',
                    border: 'none',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {preset}g
                </button>
              ))}
            </div>
          </div>

          {/* Comparison Cards: Market vs ML Jewellers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            marginBottom: '1.25rem'
          }}>
            {/* Traditional Jeweller Card */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.76rem', color: '#a1a1aa', fontWeight: 600 }}>
                  Traditional Retail Jeweller
                </span>
                <span style={{ fontSize: '0.72rem', color: '#fb7185', background: 'rgba(244, 63, 94, 0.1)', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
                  High Wastage ({currentTier.marketWastage}%)
                </span>
              </div>

              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Gold Value ({parsedWeight}g @ {formatINR(currentRate)}):</span>
                  <span>{formatINR(rawMetalValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Wastage & Making ({currentTier.marketWastage}%):</span>
                  <span style={{ color: '#fb7185' }}>+{formatINR(marketWastageAmt)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>GST (3%):</span>
                  <span>+{formatINR(marketGst)}</span>
                </div>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '0.3rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>
                  <span>Estimated Total:</span>
                  <span style={{ textDecoration: 'line-through', color: '#a1a1aa' }}>
                    {formatINR(marketTotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* ML Jewellers 24KT Club Card */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(212, 175, 55, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
              borderRadius: 'var(--radius-md)',
              border: '1.5px solid var(--gold-400)',
              padding: '1rem',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '12px',
                background: 'var(--gold-gradient)',
                color: '#000',
                fontSize: '0.62rem',
                fontWeight: 900,
                padding: '0.15rem 0.5rem',
                borderRadius: '999px',
                letterSpacing: '0.04em'
              }}>
                24KT CLUB PRICE
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.76rem', color: 'var(--gold-300)', fontWeight: 700 }}>
                  ML Jewellers (Bharat Digital Jeweller)
                </span>
                <span style={{ fontSize: '0.72rem', color: '#34d399', background: 'rgba(16, 185, 129, 0.2)', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 700 }}>
                  Flat {currentTier.ourWastage}% Wastage
                </span>
              </div>

              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Gold Value ({parsedWeight}g @ {formatINR(currentRate)}):</span>
                  <span>{formatINR(rawMetalValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Wastage & Making ({currentTier.ourWastage}%):</span>
                  <span style={{ color: '#34d399' }}>+{formatINR(ourWastageAmt)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>GST (3%):</span>
                  <span>+{formatINR(ourGst)}</span>
                </div>
                <div style={{ height: '1px', background: 'rgba(212,175,55,0.2)', margin: '0.3rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', fontWeight: 800, color: 'var(--gold-400)' }}>
                  <span>Your Net Price:</span>
                  <span>{formatINR(ourTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Huge Savings Callout Box */}
          <div style={{
            background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.2) 0%, rgba(212, 175, 55, 0.2) 100%)',
            border: '1px solid #10b981',
            borderRadius: 'var(--radius-md)',
            padding: '0.85rem 1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.25rem',
            flexWrap: 'wrap',
            gap: '0.8rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <TrendingDown size={28} color="#34d399" />
              <div>
                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a7f3d0', fontWeight: 700 }}>
                  Direct Member Benefit
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#fff' }}>
                  You Save: <span style={{ color: '#34d399' }}>{formatINR(totalSavings)}</span>
                </div>
              </div>
            </div>

            <div style={{ fontSize: '0.76rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <ShieldCheck size={16} color="var(--gold-400)" />
              100% BIS Hallmarked HUID Guarantee
            </div>
          </div>

          {/* Action CTAs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.75rem' }}>
            <button
              onClick={handleWhatsAppOrder}
              type="button"
              style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: '0.85rem',
                fontSize: '0.88rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 14px rgba(37, 211, 102, 0.35)'
              }}
            >
              <MessageCircle size={18} />
              Lock Rate & Order on WhatsApp
            </button>

            <button
              onClick={() => {
                setIsWastageModalOpen(false);
                const catalogEl = document.getElementById('catalog-section');
                if (catalogEl) catalogEl.scrollIntoView({ behavior: 'smooth' });
              }}
              type="button"
              className="btn btn-outline-gold"
              style={{
                padding: '0.85rem',
                fontSize: '0.88rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem'
              }}
            >
              Browse Catalog <ArrowRight size={16} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
